import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createCustomer,
  deleteCustomer,
  getAllCustomers,
  getSingleCustomer,
  updateCustomer,
} from '@/services/core.service'
import type { ICustomerFilters } from '@/types/rc/filters'
import type { CustomerForm } from '@/types/rc/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { Reactive } from 'vue'

export const useCustomers = () => {
  const client = useQueryClient()

  const getCustomersQuery = (filters: Reactive<ICustomerFilters>) =>
    useQuery({
      queryKey: ['customers', filters],
      queryFn: () => getAllCustomers(filters),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    })

  const getSingleCustomerQuery = (customerId: number) =>
    useQuery({
      queryKey: ['customers', customerId],
      queryFn: () => getSingleCustomer(customerId),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled: !!customerId,
    })

  const createCustomerMutation = useMutation({
    mutationFn: (form: CustomerForm) => createCustomer(form),
    onError: NotifyErrorWrapper('Failed to create customer'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['customers'] })
      NotifySuccess(`Customer created successfully`)
    },
  })

  const updateCustomerMutation = useMutation({
    mutationFn: ({ customerId, form }: { customerId: number; form: CustomerForm }) => updateCustomer(customerId, form),
    onError: NotifyErrorWrapper('Failed to update customer'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['customers'] })
      NotifySuccess(`Customer updated successfully`)
    },
  })

  const deleteCustomerMutation = useMutation({
    mutationFn: (id: number) => deleteCustomer(id),
    onError: NotifyErrorWrapper('Failed to delete customer'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['customers'] })
      NotifySuccess(`Customer deleted successfully`)
    },
  })

  return {
    getCustomersQuery,
    getSingleCustomerQuery,
    createCustomerMutation,
    updateCustomerMutation,
    deleteCustomerMutation,
  }
}
